import React, { useEffect, useState, useCallback } from 'react'
import { FormSkeleton } from './PageSkeletons'
import {
  CheckCircle,
  XCircle,
  Save,
  Loader2,
  Zap,
  Wifi,
  PlugZap,
  Layers,
  ShoppingCart,
  ChevronDown,
  Search,
  X,
  BarChart2,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react'
import { templatesApi, nsApi } from '../services/api'
import { nsEntityNouns, nsEntityDisplayName } from '../utils/nsEntityLabels'
import Toggle from './Toggle'

// ─── Toggle row ────────────────────────────────────────────────────────────────

function ToggleRow({ id, label, description, checked, onChange, disabled }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3.5 border-b border-gray-50 dark:border-gray-800 last:border-b-0">
      <div className="flex-1 min-w-0">
        <label htmlFor={id} className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer">{label}</label>
        {description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>}
      </div>
      <Toggle checked={checked} onChange={() => onChange(!checked)} disabled={disabled} />
    </div>
  )
}

// ─── Simple dropdown for subsidiary / location / currency ─────────────────────

function LookupSelect({ value, options, onChange, placeholder, loading }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = React.useRef(null)

  const selected = options.find(o => String(o.id) === String(value))

  const filtered = React.useMemo(() => {
    if (!query.trim()) return options.slice(0, 60)
    const q = query.toLowerCase()
    return options.filter(o => o.name?.toLowerCase().includes(q) || String(o.id).includes(q)).slice(0, 60)
  }, [options, query])

  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => !loading && setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm bg-white dark:bg-gray-800 ${loading ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
      >
        <span className={selected ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}>
          {loading ? 'Loading…' : selected ? selected.name : placeholder}
        </span>
        {loading ? <Loader2 className="h-3.5 w-3.5 text-gray-400 animate-spin" /> : <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />}
      </button>
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-xl max-h-56 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-1.5 px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <Search className="h-3 w-3 text-gray-400" />
              <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search…" autoFocus className="flex-1 text-xs bg-transparent focus:outline-none" />
              {query && <button onClick={() => setQuery('')}><X className="h-3 w-3 text-gray-400" /></button>}
            </div>
          </div>
          <div className="overflow-y-auto">
            <button onClick={() => { onChange(''); setOpen(false); setQuery('') }} className="w-full text-left px-3 py-2 text-xs text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700">— None —</button>
            {filtered.map(o => (
              <button key={o.id} onClick={() => { onChange(String(o.id)); setOpen(false); setQuery('') }}
                className="w-full text-left px-3 py-2 hover:bg-purple-50 dark:hover:bg-purple-900/20 flex items-center justify-between">
                <span className="text-sm text-gray-900 dark:text-gray-100 truncate">{o.name}</span>
                <span className="ml-2 text-xs text-gray-400 font-mono flex-shrink-0">{o.id}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

function TemplateNSSettings({ templateId, templateName, nsEntityType = 'purchase_order' }) {
  // Every label below names the template's OWN document type. This card used to
  // read "Purchase Order" unconditionally, which is simply wrong on a Vendor
  // Bid template, and the validation/drift copy referred to sampling "POs"
  // when it now samples that type's own records.
  const nouns = nsEntityNouns(nsEntityType)
  const docName = nsEntityDisplayName(nsEntityType)
  const [loading, setLoading]   = useState(true)
  const [saving, setSaving]     = useState(false)
  const [saved, setSaved]       = useState(false)
  const [error, setError]       = useState('')

  const [nsEnabled, setNsEnabled]                           = useState(false)
  // Was its own hardcoded useState('purchase_order') with no setter — every
  // save from this screen sent ns_entity_type: 'purchase_order' regardless of
  // the template's real document type, silently resetting a Vendor Bid (or
  // any admin-configured generic record type) template back to Purchase
  // Order on the very next settings save. The template's real type is
  // already the nsEntityType prop (used correctly for this card's own
  // labels above) — this just needed to read the same value instead of a
  // second, stale copy of it.
  const entityType = nsEntityType
  // Record types this deployment can post right now. The readiness check
  // used to be `!!entityType`, which every template passes — the column
  // defaults to purchase_order — so a template whose generic record type was
  // de-registered still showed as ready.
  const [postableTypeKeys, setPostableTypeKeys] = useState(null)
  useEffect(() => {
    let cancelled = false
    nsApi.getEntityTypes()
      .then(({ data }) => {
        if (!cancelled) setPostableTypeKeys((data?.entity_types || []).map(t => String(t.ns_entity_type_key || '').toLowerCase()))
      })
      .catch(() => { if (!cancelled) setPostableTypeKeys(null) })
    return () => { cancelled = true }
  }, [])
  const entityTypeAvailable = postableTypeKeys
    ? postableTypeKeys.includes(String(entityType || '').toLowerCase())
    : !!entityType
  const [requireApproval, setRequireApproval]               = useState(true)
  const [validateBeforePosting, setValidateBeforePosting]   = useState(true)
  const [blockOnErrors, setBlockOnErrors]                   = useState(true)
  const [maxPostAttempts, setMaxPostAttempts]               = useState(3)

  // Auto-refresh preference — same idea as Business Central's per-connection
  // auto-sync (on/off + how often), but a genuinely custom schedule instead
  // of a locked 6/12/24 dropdown: either "every N hours" with N freely
  // typed, or "monthly on day D" for a calendar-aligned schedule. Defaults
  // match the worker's own pre-existing defaults (on, hourly, 24h) so a
  // template with no explicit setting behaves exactly as it did before this
  // control existed.
  const [autoRefreshEnabled, setAutoRefreshEnabled]         = useState(true)
  const [autoRefreshScheduleType, setAutoRefreshScheduleType] = useState('hours') // 'hours' | 'monthly'
  const [autoRefreshInterval, setAutoRefreshInterval]       = useState(24)
  const [autoRefreshDayOfMonth, setAutoRefreshDayOfMonth]   = useState(1)
  const [autoRefreshSaving, setAutoRefreshSaving]           = useState(false)
  const [autoRefreshError, setAutoRefreshError]             = useState('')

  // Validation state
  const [validating, setValidating]     = useState(false)
  const [validationResult, setValidationResult] = useState(null)

  // Drift check state
  const [driftChecking, setDriftChecking]   = useState(false)
  const [driftResult, setDriftResult]       = useState(null)

  // NS defaults
  const [defaultSubsidiaryId, setDefaultSubsidiaryId] = useState('')
  const [defaultLocationId, setDefaultLocationId]     = useState('')
  const [defaultCurrencyId, setDefaultCurrencyId]     = useState('')
  const [defaultTermsId, setDefaultTermsId]           = useState('')
  const [defaultShippingTermsId, setDefaultShippingTermsId] = useState('')
  // The opt-in. Off until an admin turns it on, per template: these five
  // values have been editable and saved since the integration shipped but
  // nothing ever posted them, and every NetSuite template already carries a
  // default subsidiary written at creation time — so honouring them
  // automatically would change what every live template sends on the next
  // deploy, using a value that may have been picked for a different purpose.
  const [applyDefaults, setApplyDefaults] = useState(false)

  // NS lookups
  const [lookups, setLookups]       = useState({})
  const [lookupsLoading, setLookupsLoading] = useState(false)

  const subsidiaries = React.useMemo(() => lookups.subsidiaries || [], [lookups])
  const currencies   = React.useMemo(() => lookups.currencies || [], [lookups])
  const allLocations = React.useMemo(() => lookups.locations || [], [lookups])
  const terms        = React.useMemo(() => lookups.terms || [], [lookups])
  const shippingTerms = React.useMemo(() => lookups.shipping_terms || [], [lookups])

  // On a Purchase Order NetSuite derives currency and payment terms from the
  // vendor and rejects an overriding value, so the server deliberately never
  // binds a default to either (see resolve_template_defaults). Saying so here
  // is the difference between a control that looks broken and one that is
  // honestly labelled — the value still saves, it just cannot be posted for
  // this document type.
  const vendorDerivedHere = nsEntityType === 'purchase_order'

  const filteredLocations = React.useMemo(() => {
    if (!defaultSubsidiaryId) return allLocations
    // get_locations returns `subsidiaries: [ids]`, not a scalar `subsidiaryId`
    // — a OneWorld location can be shared across several subsidiaries, which is
    // why the backend unions locationsubsidiarymap to build that list. This
    // filter read `l.subsidiaryId`, a key that has never existed on these rows,
    // so `!l.subsidiaryId` was always true and the dropdown showed all 137
    // locations whatever subsidiary was picked.
    //
    // An empty list still means "not restricted", so it stays visible: that is
    // the pre-existing intent of the `!l.subsidiaryId` escape hatch, and an
    // over-broad list is a usability issue the user can see and correct whereas
    // an empty one looks like the location does not exist.
    return allLocations.filter(l => {
      const subs = l.subsidiaries
      if (!Array.isArray(subs) || subs.length === 0) return true
      return subs.map(String).includes(defaultSubsidiaryId)
    })
  }, [allLocations, defaultSubsidiaryId])

  // Connection state
  const [connection, setConnection] = useState({ connected: false, health: 'unknown', account_id: '' })
  const [testing, setTesting]       = useState(false)
  const [testResult, setTestResult] = useState(null)

  useEffect(() => {
    nsApi.getConnectionInfo().then(res => setConnection(res.data || {})).catch(() => {})
  }, [])

  // Load template settings
  useEffect(() => {
    let active = true
    if (!templateId) return
    setLoading(true); setError('')
    templatesApi.get(templateId)
      .then(res => {
        if (!active) return
        const t = res.data || {}
        setNsEnabled(Boolean(t.ns_enabled))
        setRequireApproval(t.ns_require_approval !== undefined ? Boolean(t.ns_require_approval) : true)
        setValidateBeforePosting(t.ns_validate_before_posting !== undefined ? Boolean(t.ns_validate_before_posting) : true)
        setBlockOnErrors(t.ns_block_on_errors !== undefined ? Boolean(t.ns_block_on_errors) : true)
        setMaxPostAttempts(Number(t.ns_max_post_attempts) || 3)
        setDefaultSubsidiaryId(t.ns_default_subsidiary_id || '')
        setDefaultLocationId(t.ns_default_location_id || '')
        setDefaultCurrencyId(t.ns_default_currency_id || '')
        setDefaultTermsId(t.ns_default_terms_id || '')
        setDefaultShippingTermsId(t.ns_default_shipping_terms_id || '')
        setApplyDefaults(Boolean(t.ns_apply_defaults))
        setAutoRefreshEnabled(t.ns_auto_refresh_enabled !== undefined ? Boolean(t.ns_auto_refresh_enabled) : true)
        setAutoRefreshScheduleType(t.ns_auto_refresh_schedule_type === 'monthly' ? 'monthly' : 'hours')
        setAutoRefreshInterval(t.ns_auto_refresh_interval_hours || 24)
        setAutoRefreshDayOfMonth(t.ns_auto_refresh_day_of_month || 1)
      })
      .catch(err => { if (!active) return; setError(err?.response?.data?.error || 'Failed to load settings.') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [templateId])

  // Load NS lookups when enabled
  useEffect(() => {
    if (!nsEnabled) return
    setLookupsLoading(true)
    nsApi.getLookups()
      .then(res => setLookups(res.data || {}))
      .catch(() => {})
      .finally(() => setLookupsLoading(false))
  }, [nsEnabled])

  const save = useCallback(async (patch = {}) => {
    if (!templateId) return
    setSaving(true); setSaved(false); setError('')
    try {
      const res = await templatesApi.update(templateId, {
        ns_enabled: nsEnabled,
        ns_entity_type: entityType,
        ns_require_approval: requireApproval,
        ns_validate_before_posting: validateBeforePosting,
        ns_block_on_errors: blockOnErrors,
        ns_max_post_attempts: maxPostAttempts,
        ns_default_subsidiary_id: defaultSubsidiaryId || null,
        ns_default_location_id: defaultLocationId || null,
        ns_default_currency_id: defaultCurrencyId || null,
        ns_default_terms_id: defaultTermsId || null,
        ns_default_shipping_terms_id: defaultShippingTermsId || null,
        ns_apply_defaults: applyDefaults,
        ...patch,
      })
      const t = res.data || {}
      setNsEnabled(Boolean(t.ns_enabled))
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save settings.')
    } finally { setSaving(false) }
  }, [templateId, nsEnabled, entityType, requireApproval, validateBeforePosting, blockOnErrors,
      maxPostAttempts, defaultSubsidiaryId, defaultLocationId, defaultCurrencyId, defaultTermsId,
      defaultShippingTermsId, applyDefaults])

  const handleToggleNs = async (val) => {
    setNsEnabled(val)
    if (!val) await save({ ns_enabled: val })
  }

  // Saves immediately on change, same as BC's auto-sync control, rather than
  // waiting for a separate "Save" action — a background schedule preference
  // like this one is easy to forget to save deliberately. Takes every field
  // together (not just the one that changed) so each call is a complete,
  // consistent schedule rather than racing partial updates.
  const saveAutoRefresh = async (next) => {
    const nextEnabled = next.enabled ?? autoRefreshEnabled
    const nextScheduleType = next.scheduleType ?? autoRefreshScheduleType
    const nextInterval = next.intervalHours ?? autoRefreshInterval
    const nextDayOfMonth = next.dayOfMonth ?? autoRefreshDayOfMonth
    setAutoRefreshEnabled(nextEnabled)
    setAutoRefreshScheduleType(nextScheduleType)
    setAutoRefreshInterval(nextInterval)
    setAutoRefreshDayOfMonth(nextDayOfMonth)
    setAutoRefreshSaving(true)
    setAutoRefreshError('')
    try {
      await templatesApi.update(templateId, {
        ns_auto_refresh_enabled: nextEnabled,
        ns_auto_refresh_schedule_type: nextScheduleType,
        ns_auto_refresh_interval_hours: nextInterval,
        ns_auto_refresh_day_of_month: nextDayOfMonth,
      })
    } catch (err) {
      setAutoRefreshError(err?.response?.data?.error || 'Failed to save auto-refresh setting.')
    } finally {
      setAutoRefreshSaving(false)
    }
  }

  if (loading) {
    return <FormSkeleton />
  }

  return (
    <div className="space-y-4 max-w-2xl">

      {/* Feedback */}
      {error && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}
      {saved && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
          <p className="text-sm text-green-700 dark:text-green-300 font-medium">Settings saved.</p>
        </div>
      )}

      {/* Card 1: Enable NS */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${nsEnabled ? 'bg-purple-50 dark:bg-purple-900/20' : 'bg-gray-100 dark:bg-gray-800'}`}>
              <Layers className={`h-5 w-5 transition-colors ${nsEnabled ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400'}`} />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">NetSuite Integration</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {nsEnabled ? 'Active — requests using this template will route to NS.' : 'Enable to send processed documents to NetSuite.'}
              </p>
            </div>
          </div>
          <Toggle checked={nsEnabled} onChange={() => handleToggleNs(!nsEnabled)} />
        </div>

        {!nsEnabled && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 flex flex-col items-center py-6 gap-2">
            <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-1">
              <Layers className="h-6 w-6 text-gray-300 dark:text-gray-600" />
            </div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">NS not enabled for this template</p>
            <p className="text-xs text-gray-400 dark:text-gray-500">Toggle above to configure NetSuite posting.</p>
          </div>
        )}
      </div>

      {/* Sections only visible when NS is enabled */}
      {nsEnabled && (
        <>
          {/* Card 2: Posting Rules */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-purple-50 dark:bg-purple-900/20">
                <Zap className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Posting Rules</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Control how documents are posted to NetSuite.</p>
              </div>
            </div>
            <div className="px-5 py-1">
              <ToggleRow id="ns-require-approval" label="Require Approval Before Posting" description="An approver must approve the request before it can be posted to NS." checked={requireApproval} onChange={setRequireApproval} />
              <ToggleRow id="ns-validate-before-posting" label="Validate Before Posting" description="Run field and business rule validation before sending to NS." checked={validateBeforePosting} onChange={setValidateBeforePosting} />
              <ToggleRow id="ns-block-on-errors" label="Block Posting on Errors" description="Prevent posting if any validation errors are found." checked={blockOnErrors} onChange={setBlockOnErrors} />
            </div>
            {/* Retry ceiling. Was the bare literal 3 in nine places across the
                three retry paths — including the user-facing error string — so
                it could not be raised for a template whose failures an operator
                is actively fixing, nor lowered for a flaky endpoint. Clamped to
                the same 1..10 window the service and repository enforce. */}
            <div className="px-5 pb-4 pt-1 border-t border-gray-100 dark:border-gray-800">
              <label htmlFor="ns-max-post-attempts" className="block text-sm text-gray-900 dark:text-gray-100">
                Maximum post attempts
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                How many times a document may be sent to NetSuite before Retry stops offering. Counts the first attempt.
              </p>
              <input
                id="ns-max-post-attempts"
                type="number"
                min={1}
                max={10}
                value={maxPostAttempts}
                onChange={e => {
                  // Clamp on the way in so the field can never save a value the
                  // server would silently clamp to something else.
                  const raw = parseInt(e.target.value, 10)
                  setMaxPostAttempts(Number.isNaN(raw) ? 3 : Math.max(1, Math.min(raw, 10)))
                }}
                className="mt-2 w-24 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-navy-100 dark:focus:ring-navy-900"
              />
            </div>
          </div>

          {/* Card 3: Entity Type */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-purple-50 dark:bg-purple-900/20">
                  <ShoppingCart className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Record Type</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">The NetSuite record this template creates.</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    To change it, use <strong>Export JSON</strong>, edit <code>record.ns_entity_type</code> and the mappings, then <strong>Update from JSON</strong>.
                  </p>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300">
                <ShoppingCart className="h-3 w-3" /> {docName}
              </span>
            </div>
          </div>

          {/* Card 4: NS Defaults (Subsidiary, Location, Currency) */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-purple-50 dark:bg-purple-900/20">
                <Layers className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Default NS Values</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Values documents rarely state explicitly. Set once, applied to every document of this type that leaves them blank.</p>
              </div>
            </div>

            {/* The opt-in sits ABOVE the values, not below them: what these
                fields do depends entirely on it, and an admin reading top to
                bottom should know that before choosing any of them. */}
            <div className="px-5 py-1 border-b border-gray-100 dark:border-gray-800">
              <ToggleRow
                id="ns-apply-defaults"
                label="Apply these defaults when posting"
                description={applyDefaults
                  ? 'A field left blank by the document is filled from the value set below, and shown on the review screen as coming from this template.'
                  : 'Off — these values are saved but nothing is filled in. Turn on to start using them.'}
                checked={applyDefaults}
                onChange={setApplyDefaults}
              />
            </div>

            <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Default Subsidiary</label>
                <LookupSelect value={defaultSubsidiaryId} options={subsidiaries} onChange={v => { setDefaultSubsidiaryId(v); if (defaultLocationId) setDefaultLocationId('') }} placeholder="Select subsidiary…" loading={lookupsLoading} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Default Location</label>
                <LookupSelect value={defaultLocationId} options={filteredLocations} onChange={setDefaultLocationId} placeholder="Select location…" loading={lookupsLoading} />
                {defaultSubsidiaryId && (
                  <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Limited to locations in the selected subsidiary.</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Default Shipping Terms</label>
                <LookupSelect value={defaultShippingTermsId} options={shippingTerms} onChange={setDefaultShippingTermsId} placeholder="Select shipping terms…" loading={lookupsLoading} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Default Currency</label>
                <LookupSelect value={defaultCurrencyId} options={currencies} onChange={setDefaultCurrencyId} placeholder="Select currency…" loading={lookupsLoading} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Default Payment Terms</label>
                <LookupSelect value={defaultTermsId} options={terms} onChange={setDefaultTermsId} placeholder="Select payment terms…" loading={lookupsLoading} />
              </div>
            </div>

            {vendorDerivedHere && (defaultCurrencyId || defaultTermsId) && (
              <div className="mx-5 mb-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
                <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  On a {docName}, NetSuite sets <strong>Currency</strong> and <strong>Payment Terms</strong> from the vendor and rejects any value sent for them.
                  These two are saved here but will not be applied when posting this record type.
                </p>
              </div>
            )}
          </div>

          {/* Card 5: Connection */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-50 dark:bg-emerald-900/20">
                <PlugZap className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">NetSuite Connection</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">OAuth1 TBA connection to your NetSuite account.</p>
              </div>
              {connection.lastTestedAt && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300">
                  <Wifi className="h-3 w-3" /> Last tested OK
                </span>
              )}
            </div>
            <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Account ID</label>
                <div className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200">{connection.account_id || '—'}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status</label>
                <div className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200">{connection.connected ? 'Connected' : 'Not tested'}</div>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 flex items-center gap-3 flex-wrap">
              <button
                onClick={async () => {
                  setTesting(true); setTestResult(null)
                  try {
                    const res = await nsApi.testConnection()
                    const r = res.data
                    setTestResult(r)
                    if (r.connected) setConnection(prev => ({ ...prev, lastTestedAt: new Date().toISOString(), connected: true, health: 'healthy', account_id: r.account_id || prev.account_id }))
                  } catch (e) { setTestResult({ connected: false, error: e.message }) }
                  setTesting(false)
                }}
                disabled={testing}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed bg-purple-600"
              >
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}
                {testing ? 'Testing…' : 'Test Connection'}
              </button>
              {testResult?.connected && <span className="text-xs text-green-700 dark:text-green-300 flex items-center gap-1.5"><CheckCircle className="h-3.5 w-3.5" /> Connected — {testResult.account_id}</span>}
              {testResult && !testResult.connected && <span className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1.5"><XCircle className="h-3.5 w-3.5" /> {testResult.error}</span>}
            </div>
          </div>

          {/* Card 6: Readiness */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-amber-50 dark:bg-amber-900/20">
                <CheckCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Template Readiness</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">All checks must pass before documents can be posted to NS.</p>
              </div>
            </div>
            <div className="px-5 py-2">
              {(() => {
                const checks = [
                  { label: 'NS Enabled',            ok: nsEnabled },
                  { label: 'Record Type Available', ok: entityTypeAvailable },
                  { label: 'Connection Tested',     ok: !!connection.lastTestedAt },
                  { label: 'Default Subsidiary Set', ok: !!defaultSubsidiaryId },
                ]
                const passed = checks.filter(c => c.ok).length
                const allOk  = passed === checks.length
                return (
                  <>
                    {checks.map(c => (
                      <div key={c.label} className="flex items-center justify-between py-2.5 border-b border-gray-50 dark:border-gray-800 last:border-b-0">
                        <span className={`text-sm ${c.ok ? 'text-gray-800 dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'}`}>{c.label}</span>
                        {c.ok ? <CheckCircle className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-gray-300 dark:text-gray-600" />}
                      </div>
                    ))}
                    <div className={`mt-3 mb-2 px-3 py-2.5 rounded-lg text-center text-sm font-semibold ${allOk ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'}`}>
                      {allOk ? `✓ Ready for Processing (${passed}/${checks.length})` : `⚠ Template Not Ready (${passed}/${checks.length})`}
                    </div>
                  </>
                )
              })()}
            </div>
          </div>

          {/* Card 7: Validate Mapping */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-purple-50 dark:bg-purple-900/20">
                <BarChart2 className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Validate Field Mapping</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Score each template field against the last 5 real {nouns.plural} from NetSuite.</p>
              </div>
              <button
                type="button"
                disabled={validating}
                onClick={async () => {
                  setValidating(true)
                  setValidationResult(null)
                  try {
                    const res = await nsApi.validateMapping(templateId)
                    setValidationResult(res.data)
                  } catch (e) {
                    setValidationResult({ success: false, error: e?.response?.data?.error || 'Validation failed' })
                  } finally {
                    setValidating(false)
                  }
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {validating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BarChart2 className="h-3.5 w-3.5" />}
                {validating ? 'Validating…' : 'Run Validation'}
              </button>
            </div>
            {validationResult && (
              <div className="px-5 py-4 space-y-3">
                {!validationResult.success ? (
                  <p className="text-sm text-red-600 dark:text-red-400">{validationResult.error}</p>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Overall score</span>
                      <span className={`text-sm font-semibold ${validationResult.overall_score >= 0.6 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {Math.round((validationResult.overall_score || 0) * 100)}%
                      </span>
                    </div>
                    {validationResult.field_scores && Object.entries(validationResult.field_scores).map(([key, score]) => (
                      <div key={key} className="space-y-0.5">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-mono text-gray-700 dark:text-gray-300">{key}</span>
                          <span className={score >= 0.6 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>{Math.round(score * 100)}%</span>
                        </div>
                        <div className="w-full h-1.5 rounded-full bg-gray-100 dark:bg-gray-800">
                          <div className={`h-1.5 rounded-full ${score >= 0.6 ? 'bg-green-500' : 'bg-red-500'}`} style={{ width: `${Math.round(score * 100)}%` }} />
                        </div>
                      </div>
                    ))}
                    {validationResult.warnings?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {validationResult.warnings.map((w, i) => (
                          <div key={i} className="flex items-start gap-1.5">
                            <AlertTriangle className="h-3 w-3 text-amber-500 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-amber-600 dark:text-amber-400">{w}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-gray-400 dark:text-gray-500">Sampled {validationResult.sample_size} {validationResult.sample_size === 1 ? nouns.singular : nouns.plural} from NetSuite</p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Card 8: Drift Check */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                driftResult?.drift_status === 'drifted'   ? 'bg-red-50 dark:bg-red-900/20' :
                driftResult?.drift_status === 'warning'   ? 'bg-amber-50 dark:bg-amber-900/20' :
                'bg-gray-100 dark:bg-gray-800'
              }`}>
                <RefreshCw className={`h-4 w-4 ${
                  driftResult?.drift_status === 'drifted' ? 'text-red-500' :
                  driftResult?.drift_status === 'warning' ? 'text-amber-500' :
                  'text-gray-400'
                }`} />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Drift Monitor</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Check if any mapped fields have high null rates in the last 50 {nouns.plural}.</p>
              </div>
            </div>

            {/* Auto-refresh schedule — same on/off idea as Business Central's
                auto-sync, but a real custom schedule instead of a locked
                6/12/24 dropdown: type any hour interval, or switch to a
                specific day of the month. The background worker already ran
                this same drift check on every NS template daily with no way
                to turn it off or change the cadence; this exposes that as an
                actual setting instead of a hardcoded constant. */}
            <div className="px-5 py-3.5 border-t border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <Toggle
                    checked={autoRefreshEnabled}
                    onChange={() => saveAutoRefresh({ enabled: !autoRefreshEnabled })}
                    disabled={autoRefreshSaving}
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Auto-refresh fields &amp; validations</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Automatically re-checks drift and refreshes local validation rules from NetSuite on a custom schedule.
                    </p>
                  </div>
                </div>
                {autoRefreshSaving && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400 flex-shrink-0" />}
              </div>

              {autoRefreshEnabled && (
                <div className="mt-3 pl-[52px] flex items-center gap-2 flex-wrap">
                  <select
                    value={autoRefreshScheduleType}
                    onChange={e => saveAutoRefresh({ scheduleType: e.target.value })}
                    disabled={autoRefreshSaving}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500 disabled:opacity-50"
                  >
                    <option value="hours">Every…</option>
                    <option value="monthly">Monthly on day…</option>
                  </select>

                  {autoRefreshScheduleType === 'hours' ? (
                    <>
                      <input
                        type="number"
                        min={1}
                        max={2160}
                        value={autoRefreshInterval}
                        onChange={e => setAutoRefreshInterval(e.target.value === '' ? '' : Number(e.target.value))}
                        onBlur={() => saveAutoRefresh({ intervalHours: Math.max(1, Math.min(2160, Number(autoRefreshInterval) || 24)) })}
                        disabled={autoRefreshSaving}
                        className="w-20 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500 disabled:opacity-50"
                      />
                      <span className="text-sm text-gray-500 dark:text-gray-400">hour{Number(autoRefreshInterval) === 1 ? '' : 's'}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-gray-500 dark:text-gray-400">Day</span>
                      <input
                        type="number"
                        min={1}
                        max={28}
                        value={autoRefreshDayOfMonth}
                        onChange={e => setAutoRefreshDayOfMonth(e.target.value === '' ? '' : Number(e.target.value))}
                        onBlur={() => saveAutoRefresh({ dayOfMonth: Math.max(1, Math.min(28, Number(autoRefreshDayOfMonth) || 1)) })}
                        disabled={autoRefreshSaving}
                        className="w-16 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500 disabled:opacity-50"
                      />
                      <span className="text-sm text-gray-500 dark:text-gray-400">of every month</span>
                    </>
                  )}
                </div>
              )}
            </div>
            {autoRefreshError && (
              <div className="px-5 pb-3 -mt-1">
                <span className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" /> {autoRefreshError}
                </span>
              </div>
            )}

            {driftResult && (
              <div className="px-5 py-4 space-y-2">
                {driftResult.drift_status === 'ok' && (
                  <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
                    <CheckCircle className="h-4 w-4" />
                    All mapped fields are healthy ({driftResult.sample_size} {nouns.abbrevPlural} sampled).
                  </div>
                )}
                {driftResult.drift_status === 'insufficient_data' && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Not enough data — only {driftResult.sample_size} {nouns.abbrevPlural} found (need ≥ 5).</p>
                )}
                {driftResult.drift_status === 'warning' && (
                  <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-amber-700 dark:text-amber-300">Some fields showing reduced match rates</p>
                        <ul className="mt-1 space-y-0.5">
                          {(driftResult.warning_fields || []).map(f => (
                            <li key={f.field} className="text-xs text-amber-600 dark:text-amber-400 font-mono">{f.field} — {Math.round(f.null_rate * 100)}% null rate</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                {driftResult.drift_status === 'drifted' && (
                  <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700">
                    <div className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-red-700 dark:text-red-300">Mapping has drifted — fields no longer matching your {nouns.abbrevPlural}</p>
                        <ul className="mt-1 space-y-0.5">
                          {(driftResult.drifted_fields || []).map(f => (
                            <li key={f.field} className="text-xs text-red-600 dark:text-red-400 font-mono">{f.field} — {Math.round(f.null_rate * 100)}% null rate</li>
                          ))}
                        </ul>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Consider uploading a new sample document and re-mapping fields.</p>
                      </div>
                    </div>
                  </div>
                )}
                {driftResult.error && (
                  <p className="text-sm text-red-600 dark:text-red-400">{driftResult.error}</p>
                )}
              </div>
            )}

            {/* Bottom action bar — same placement/style as BC's "Sync Now"
                bar, instead of the button living cramped in the header. */}
            <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                disabled={driftChecking}
                onClick={async () => {
                  setDriftChecking(true)
                  setDriftResult(null)
                  try {
                    const res = await nsApi.checkDrift(templateId)
                    setDriftResult(res.data)
                  } catch (e) {
                    setDriftResult({ drift_status: 'unknown', error: e?.response?.data?.error || 'Drift check failed' })
                  } finally {
                    setDriftChecking(false)
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed bg-[var(--token-primary,#1946FA)]"
              >
                {driftChecking ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                {driftChecking ? 'Checking…' : 'Check Drift Now'}
              </button>
            </div>
          </div>

          {/* Save button */}
          <div className="flex justify-end pt-1">
            <button onClick={() => save()} disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-lg transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed bg-purple-600">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default TemplateNSSettings
